import BodyText from '@lyra/core/components/BodyText'
import DropdownButton from '@lyra/core/components/DropdownButton'
import { MoreVertical } from '@lyra/core/components/Icon'
import { createColumnHelper, TableData, TableProps } from '@lyra/core/components/Table'
import Table from '@lyra/core/components/Table'
import { SECONDS_IN_DAY } from '@lyra/core/constants/time'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatUSD from '@lyra/core/utils/formatUSD'
import InstrumentCell from '@lyra/web/components/common/InstrumentCell'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { Order } from '@lyra/web/src/constants/order'
import { getInstrumentPagePath } from '@lyra/web/utils/instruments'
import { formatOrderType } from '@lyra/web/utils/order'
import { getUtcDateFromSecs } from '@lyra/web/utils/time'
import { useRouter } from 'next/navigation'
import React, { useMemo } from 'react'
import { YStack } from 'tamagui'

import OpenOrderActions from './OpenOrderActions'

type Props = {
  orders: Order[]
} & Omit<TableProps<Order>, 'data' | 'columns'>

const OpenOrdersTable = ({ orders, ...tableProps }: Props): JSX.Element => {
  const { push } = useRouter()
  const { subaccount, cancelAllOrders } = useSubaccount()

  const rows: TableData<Order>[] = useMemo(() => {
    console.debug('open orders', orders)
    return orders.map((order) => {
      const path = getInstrumentPagePath(order.instrument_name)
      return {
        ...order,
        onPress: () => push(path),
      }
    })
  }, [orders, push])

  const hasOrdersToCancel = useMemo(() => {
    return rows.some((row) => !row.trigger_type)
  }, [rows])

  const columnHelper = createColumnHelper<Order>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('instrument_name', {
        id: 'instrument_name',
        header: 'Instrument',
        meta: {
          minWidth: 165,
        },
        cell: (props) => {
          return <InstrumentCell instrumentName={props.row.original.instrument_name} />
        },
      }),
      columnHelper.accessor('order_type', {
        id: 'order_type',
        header: 'Type / Status',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          const expDate = getUtcDateFromSecs(props.row.original.signature_expiry_sec)
          const now = new Date()
          const diffMs = Math.max(expDate.getTime() - now.getTime(), 0)
          const diffDays = Math.ceil(diffMs / (1000 * SECONDS_IN_DAY))
          return (
            <YStack>
              <BodyText>{formatOrderType(props.row.original.order_type)}</BodyText>
              <BodyText color={now >= expDate ? 'red' : 'secondary'}>Exp in {diffDays}d</BodyText>
            </YStack>
          )
        },
      }),
      columnHelper.accessor('direction', {
        id: 'direction',
        header: 'Direction',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          const isBuy = props.row.original.direction === 'buy'
          return <BodyText color={isBuy ? 'green' : 'red'}>{isBuy ? 'Buy' : 'Sell'}</BodyText>
        },
      }),
      columnHelper.accessor('amount', {
        id: 'amount',
        header: 'Amount / Filled',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          return (
            <YStack>
              <BodyText>{formatNumber(+props.row.original.amount, { minDps: 2 })}</BodyText>
              <BodyText color="secondary">
                {formatNumber(+props.row.original.filled_amount, { minDps: 2 })}
              </BodyText>
            </YStack>
          )
        },
      }),
      columnHelper.accessor('limit_price', {
        id: 'limit_price',
        header: 'Limit Price',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          return (
            <BodyText color={props.row.original.order_type === 'limit' ? 'primary' : 'secondary'}>
              {props.row.original.order_type === 'limit'
                ? formatUSD(props.row.original.limit_price)
                : '—'}
            </BodyText>
          )
        },
      }),
      columnHelper.accessor('trigger_price', {
        id: 'trigger_price',
        header: 'Trigger Price',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          return (
            <BodyText color={props.row.original.trigger_price ? 'primary' : 'secondary'}>
              {props.row.original.trigger_price ? formatUSD(props.row.original.trigger_price) : '—'}
            </BodyText>
          )
        },
      }),
      columnHelper.accessor('actions', {
        id: 'actions',
        header: () => (
          <DropdownButton size="sm" isPortaled icon={<MoreVertical />}>
            <DropdownButton.ListItem
              label="Cancel All"
              isDisabled={!subaccount || !hasOrdersToCancel}
              onPress={() => (subaccount ? cancelAllOrders(subaccount.subaccount_id) : null)}
            />
          </DropdownButton>
        ),
        meta: {
          minWidth: 120,
          maxWidth: 120,
          isActionColumn: true,
          justifyContent: 'flex-end',
        },
        cell: (props) => {
          const { onPress: _0, ...order } = props.row.original
          return <OpenOrderActions order={order} />
        },
      }),
    ],
    [cancelAllOrders, columnHelper, hasOrdersToCancel, subaccount]
  )

  return <Table emptyMessage="No orders" {...tableProps} size="lg" data={rows} columns={columns} />
}

export default OpenOrdersTable
